import { config, PLAYER_CONFIG } from '../config';
import type { DeviceManifest, DeviceRegistration } from '../types';

// Convert snake_case keys to camelCase (for Android compatibility)
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Recursively convert object keys from snake_case to camelCase
function convertKeysToCamelCase<T>(obj: unknown): T {
  if (obj === null || obj === undefined) return obj as T;
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamelCase(item)) as T;
  }
  if (typeof obj === 'object') {
    const converted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[toCamelCase(key)] = convertKeysToCamelCase(value);
    }
    return converted as T;
  }
  return obj as T;
}

// Pairing types
export interface PairingStartResponse {
  success: boolean;
  data?: {
    pairingCode: string;
    expiresAt: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface PairingStatusResponse {
  success: boolean;
  data?: {
    status: 'PENDING' | 'APPROVED';
    expiresAt: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface PairingFinishResponse {
  success: boolean;
  data?: {
    deviceId: string;
    deviceSecret: string;
    brandId: string;
    storeId: string;
    zoneId?: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

// API client for device operations
class ApiClient {
  private baseUrl: string;
  private registration: DeviceRegistration | null = null;

  constructor() {
    this.baseUrl = config.baseUrl;
  }

  setRegistration(reg: DeviceRegistration) {
    this.registration = reg;
  }

  // Get or generate device UUID
  getDeviceUuid(): string {
    const stored = localStorage.getItem('govista_device_uuid');
    if (stored) return stored;

    const uuid = crypto.randomUUID();
    localStorage.setItem('govista_device_uuid', uuid);
    return uuid;
  }

  // Generate hardware ID from browser fingerprint
  getHardwareId(): string {
    const stored = localStorage.getItem('govista_hardware_id');
    if (stored) return stored;

    // Simple fingerprint based on available info
    const info = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
    ].join('|');

    // Simple hash
    let hash = 0;
    for (let i = 0; i < info.length; i++) {
      const char = info.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    const hardwareId = `WEB-${Math.abs(hash).toString(16).toUpperCase()}`;
    localStorage.setItem('govista_hardware_id', hardwareId);
    return hardwareId;
  }

  // Start pairing - get a pairing code from the server
  async startPairing(): Promise<PairingStartResponse> {
    const deviceUuid = this.getDeviceUuid();
    const hardwareId = this.getHardwareId();

    const response = await fetch(`${this.baseUrl}/device/pair/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        deviceUuid,
        hardwareId,
      }),
    });

    return response.json();
  }

  // Check pairing status
  async checkPairingStatus(code: string): Promise<PairingStatusResponse> {
    const response = await fetch(`${this.baseUrl}/device/pair/status/${code}`);
    return response.json();
  }

  // Finish pairing - get device credentials
  async finishPairing(code: string): Promise<PairingFinishResponse> {
    const deviceUuid = this.getDeviceUuid();

    const response = await fetch(`${this.baseUrl}/device/pair/finish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pairingCode: code,
        deviceUuid,
      }),
    });

    return response.json();
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const method = (options.method || 'GET').toUpperCase();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    // Add device authentication
    if (this.registration) {
      headers['X-Device-Id'] = this.registration.deviceId;

      // Generate HMAC signature for request (timestamp in SECONDS, not milliseconds)
      const timestamp = Math.floor(Date.now() / 1000);
      const nonce = crypto.randomUUID();
      headers['X-Timestamp'] = timestamp.toString();
      headers['X-Nonce'] = nonce;

      // Sign the request using server-expected format: METHOD|path|timestamp|bodyHash
      const body = options.body as string | undefined;
      const bodyHash = body ? await this.sha256(body) : '';
      const signaturePayload = `${method}|${path}|${timestamp}|${bodyHash}`;
      const signature = await this.signHmac(signaturePayload, this.registration.deviceSecret);
      headers['X-Signature'] = signature;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PLAYER_CONFIG.API_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      return response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  // SHA-256 hash of a string
  private async sha256(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Base64 string to ArrayBuffer
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // Sign with HMAC-SHA256 using base64 secret
  private async signHmac(data: string, base64Secret: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyData = this.base64ToArrayBuffer(base64Secret);
    const messageData = encoder.encode(data);

    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', key, messageData);
    return btoa(String.fromCharCode(...new Uint8Array(signature)));
  }

  // Get device manifest
  async getManifest(): Promise<DeviceManifest> {
    const response = await this.request<{ success: boolean; data: unknown }>('/device/manifest');
    // Convert snake_case keys to camelCase (API sends snake_case for Android compatibility)
    return convertKeysToCamelCase<DeviceManifest>(response.data);
  }

  // Report device profile
  async reportProfile(data: {
    screenWidth: number;
    screenHeight: number;
    rotation: number;
    density: number;
    model?: string;
    manufacturer?: string;
    androidVersion?: string;
    capabilities: {
      supportsH265: boolean;
      supports4k: boolean;
      supportsHdr: boolean;
      maxBitrate: number;
      hasAudio: boolean;
    };
  }): Promise<void> {
    await this.request('/device/profile', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Send heartbeat (accepts all fields the server DeviceHeartbeatSchema supports)
  async sendHeartbeat(data: {
    online: boolean;
    state: 'UNPAIRED' | 'PAIRING' | 'ONLINE' | 'OFFLINE' | 'ERROR' | 'MAINTENANCE';
    errorCode?: string | null;
    errorMessage?: string | null;
    // System resources
    diskFreeMb: number;
    diskTotalMb?: number | null;
    memoryFreeMb: number;
    memoryTotalMb?: number | null;
    cpuUsagePercent?: number | null;
    cpuTemperature?: number | null;
    // Connectivity
    rssi?: number | null;
    wifiSsid?: string | null;
    connectionType?: string;
    ipAddress?: string | null;
    downloadSpeedKbps?: number | null;
    uploadSpeedKbps?: number | null;
    latencyMs?: number | null;
    // Content sync
    contentSyncPercent?: number | null;
    pendingDownloads?: number | null;
    failedDownloads?: number | null;
    manifestVersion?: number | null;
    appVersion?: string | null;
    // 3A — separate from appVersion. appVersion = IPK/Electron shell.
    // bundleVersion = the JS bundle running inside. They diverge whenever
    // a PWA service worker update lands without a shell reinstall.
    bundleVersion?: string | null;
    // Report-only memory guard. JS heap utilization for browser-based
    // players. Used for dashboard trending; NOT used to trigger reload.
    jsHeapUsedMb?: number | null;
    jsHeapLimitMb?: number | null;
    // Playback
    currentAssetId?: string;
    currentPlaylistId?: string;
    currentScheduleRuleId?: string;
    isPlaying?: boolean;
    // Power & watchdog
    lastBootAt?: string | null;
    uptimeSeconds?: number | null;
    unexpectedReboot?: boolean;
    watchdogTriggered?: boolean;
    // Display
    displayOn?: boolean;
    hdmiConnected?: boolean;
    displayResolution?: string | null;
    displayRefreshRate?: number | null;
    screenBrightness?: number | null;
    // Video playback stats
    droppedFrames?: number | null;
    bufferingCount?: number | null;
    bufferingDurationMs?: number | null;
    videoBitrateKbps?: number | null;
    videoCodec?: string | null;
    audioCodec?: string | null;
    playbackResolution?: string | null;
    // Battery & power
    batteryLevel?: number | null;
    batteryCharging?: boolean | null;
    batteryTemperature?: number | null;
    powerSource?: string | null;
    // Audio
    volumeLevel?: number | null;
    audioMuted?: boolean | null;
    // Process
    threadCount?: number | null;
    // Clock drift detection
    deviceTime?: string | null;
  }): Promise<{
    hasManifestUpdate?: boolean;
    latestManifestVersion?: number;
    serverTime?: number;
    command?: { type: 'RESTART' | 'REFRESH' | 'CLEAR_CACHE'; data?: Record<string, string> };
  }> {
    const response = await this.request<{
      success: boolean;
      data: {
        hasManifestUpdate?: boolean;
        latestManifestVersion?: number;
        serverTime?: number;
        command?: { type: 'RESTART' | 'REFRESH' | 'CLEAR_CACHE'; data?: Record<string, string> };
      };
    }>('/device/heartbeat', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Report play event (proof of play)
  async reportPlay(data: {
    assetId: string;
    playlistId: string;
    startedAt: string;
    endedAt: string;
    durationMs: number;
    completed: boolean;
  }): Promise<{ playId: string }> {
    return this.request('/device/analytics/play', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Report session
  async reportSession(data: {
    startedAt: string;
    endedAt?: string;
    assetIds: string[];
  }): Promise<void> {
    await this.request('/device/analytics/session', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Batch report plays
  async batchReportPlays(plays: Array<{
    assetId: string;
    playlistId: string;
    startedAt: string;
    endedAt: string;
    durationMs: number;
    completed: boolean;
  }>): Promise<void> {
    await this.request('/device/analytics/plays', {
      method: 'POST',
      body: JSON.stringify({ plays }),
    });
  }

  // Report a single device event
  async reportEvent(event: {
    type: string;
    data?: Record<string, string>;
    timestamp?: string;
  }): Promise<void> {
    await this.request('/device/events', {
      method: 'POST',
      body: JSON.stringify(event),
    });
  }

  // Report batch of device events
  async reportEventsBatch(events: Array<{
    type: string;
    data?: Record<string, string>;
    timestamp?: string;
  }>): Promise<void> {
    await this.request('/device/events/batch', {
      method: 'POST',
      body: JSON.stringify({ events }),
    });
  }

  // Acknowledge manifest with cached asset hashes
  async acknowledgeManifest(data: {
    manifestVersion: number;
    cachedAssets: string[];
  }): Promise<void> {
    await this.request('/device/ack', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Upload screenshot (fallback for web player core)
  async uploadScreenshot(data: {
    image: string;
    mimeType: string;
    width: number;
    height: number;
    capturedAt: string;
  }): Promise<void> {
    await this.request('/device/screenshot', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const api = new ApiClient();

// Asset fetching with caching
export async function fetchAsset(url: string): Promise<Blob> {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    PLAYER_CONFIG.ASSET_LOAD_TIMEOUT_MS
  );

  try {
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      throw new Error(`Failed to fetch asset: ${response.status}`);
    }

    return response.blob();
  } finally {
    clearTimeout(timeout);
  }
}
